import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../../redux/store'

import {
  EMPTY_STRING,
  NULL_DATE
} from '../components/global';

// Define a type for the slice state
interface RequestState {
  clearCalendarFilter: boolean,
  fiscalYear: number,
  guestRequestCancelled: boolean,
  guestRequestTime: number,
  guestRequestSaved: boolean,
  memberRequestCancelled: boolean,
  memberRequestSaved: boolean,
  maxDeparture: string,
  minArrival: string,
  notSignedUpAdded: boolean,
  offSeasonStartDate: Date,
  requestCancelled: boolean
}

// Define the initial state using that type
const initialState: RequestState = {
  clearCalendarFilter: false,
  fiscalYear: 0,
  guestRequestCancelled: false,
  guestRequestTime: 0,
  guestRequestSaved: false,
  memberRequestCancelled: false,
  memberRequestSaved: false,
  maxDeparture: EMPTY_STRING,
  minArrival: EMPTY_STRING,
  notSignedUpAdded: false,
  offSeasonStartDate: NULL_DATE,
  requestCancelled: false
}

export const requestSlice = createSlice({
  name: 'request',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setClearCalendarFilter: (state, action: PayloadAction<boolean>) => {
      state.clearCalendarFilter = action.payload
    },
    setFiscalYear: (state, action: PayloadAction<number>) => {
      state.fiscalYear = action.payload
    },
    setGuestRequestCancelled: (state, action: PayloadAction<boolean>) => {
      state.guestRequestCancelled = action.payload
    },
    setGuestRequestTime: (state, action: PayloadAction<number>) => {
      state.guestRequestTime = action.payload
    },
    setGuestRequestSaved: (state, action: PayloadAction<boolean>) => {
      state.guestRequestSaved = action.payload
    },
    setMemberRequestCancelled: (state, action: PayloadAction<boolean>) => {
      state.memberRequestCancelled = action.payload
    },
    setMemberRequestSaved: (state, action: PayloadAction<boolean>) => {
      state.memberRequestSaved = action.payload
    },
    setNotSignedUpAdded: (state, action: PayloadAction<boolean>) => {
      state.notSignedUpAdded = action.payload
    },
    setOffSeasonStartDate: (state, action: PayloadAction<Date>) => {
      state.offSeasonStartDate = action.payload
    },
    setRequestCancelled: (state, action: PayloadAction<boolean>) => {
      state.requestCancelled = action.payload
    },
    setRequestMaxDeparture: (state, action: PayloadAction<string>) => {
      state.maxDeparture = action.payload
    },
    setRequestMinArrival: (state, action: PayloadAction<string>) => {
      state.minArrival = action.payload
    }
  }
})

export const {
  setClearCalendarFilter,
  setFiscalYear,
  setGuestRequestCancelled,
  setGuestRequestTime,
  setGuestRequestSaved,
  setMemberRequestCancelled,
  setMemberRequestSaved,
  setNotSignedUpAdded,
  setOffSeasonStartDate,
  setRequestCancelled,
  setRequestMaxDeparture,
  setRequestMinArrival
} = requestSlice.actions

// Other code such as selectors can use the imported `RootState` type
export const getFiscalYear = (state: RootState) => state.request.fiscalYear;
export const getGuestRequestCancelled = (state: RootState) => state.request.guestRequestCancelled;
export const getGuestRequestSaved = (state: RootState) => state.request.guestRequestSaved;
export const getGuestRequestTime = (state: RootState) => state.request.guestRequestTime;
export const getMemberRequestCancelled = (state: RootState) => state.request.memberRequestCancelled;
export const getMemberRequestSaved = (state: RootState) => state.request.memberRequestSaved;
export const getNotSignedUpAdded = (state: RootState) => state.request.notSignedUpAdded;
// Unique name to avoid conflicts in other modules
export const getOffSeasonStartDateValue = (state: RootState) => state.request.offSeasonStartDate;
export const getRequestCancelled = (state: RootState) => state.request.requestCancelled;
export const getRequestMaxDeparture = (state: RootState) => state.request.maxDeparture;
export const getRequestMinArrival = (state: RootState) => state.request.minArrival;

export default requestSlice.reducer